<template lang="pug">
  v-layout(row wrap)
    v-snackbar(v-model="showStartImportError" color="error")
      div {{ importError }}
    v-flex(xs12)
      v-layout(row wrap)
        v-flex.mb-2(xs11)
          ul.ml-5.pb-3
            li.section-text  Select a LionDesk field from the dropdown menu in the second row to map each of your column headers to a field in LionDesk.
            li.section-text  Create new custom fields for any unique data points.
        v-flex(v-if="false" xs11)
          fields-maps(
            :fields-map="fieldsMap"
            @change="handleSelectFieldsMap"
          )
        v-flex.pt-3(xs1)
          pagination-limit.sample-size(
            label="Data sample rows"
            :limits="sampleSizes"
            :current-limit="sampleSize"
            @change:limit="setSampleSize"
          )
    v-flex.mt-1(xs12)
      v-card
        v-layout(row)
          v-flex
            v-data-table.sample-table(hide-actions :headers="headers" :items="virtualScrollVisibleItems")
              template(slot="headers" slot-scope="headers")
                tr
                  th Your columns
                  th(v-for="header in headers.headers") {{header.text}}
                tr
                  th LionDesk fields
                  th(v-for="header in headers.headers" align="left")
                    field-selector(
                        :value="fieldsMap[header.value]"
                        :hide-fields="hideFields"
                        @input="matchField($event, header.value)"
                    )
              template(slot="items" slot-scope="props")
                tr(v-if="props.index === 0 && virtualScrollStart > 0")
                  td.skeleton-loader(
                    v-for="index in headers.length + 1"
                    :style="'height:'+virtualScrollStartHeight+'px'")
                tr
                  td.sample_text {{sampleText(props.index)}}
                  td(v-for="header in headers") {{props.item[header.value]}}
                tr(v-if="props.index === virtualScrollVisibleItems.length -1 && virtualScrollStart + virtualScrollPerPage < sampleItems.length")
                  td.skeleton-loader(
                    v-for="index in headers.length + 1"
                    :style="'height:'+virtualScrollEndHeight+'px'")
    v-flex.mt-4(xs12)
      v-layout(row wrap)
        v-flex(xs12)
          span
            b.section-text Add tags to your imported contacts (Optional)
          ul.ml-5.mt-2
            li.section-text(v-html="tagInfo")
    v-flex(xs4)
      tags-picker.contact-tags(
        :disabled="false"
        v-model="globalTags"
        @save:tag="createTag"
        :tags-list="tags"
      )
    v-flex.mt-4(xs12)
      v-btn(flat @click="handlePreviousStep") Previous
      v-btn(color="primary" :disabled="disabledImport" :loading="importLoading" @click="onClickStartImport") START YOUR IMPORT
    liondesk-dialog(
      width="600px"
      v-model="showNoFullMappedFieldsWarning"
      dialog-title="IMPORT CONTACTS"
      ok-button-text="Don't import Unmapped Data"
      cancel-button-text="Go Back"
      @click:cancel="handleGoBack"
      @click:ok="onClickStartImportIgnoreUnmappedFields"
    )
      template(slot="body")
        v-container.px-0
          v-layout(row wrap)
            v-flex(xs12)
              span.unmapped-title Some fields are not mapped.
            v-flex(xs12)
              span Please match all columns to LionDesk fields. Data with unmapped columns won't be imported.
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import LiondeskDialog from '@/Core/components/LiondeskDialog';
import PaginationLimit from '@/Core/components/PaginationLimit';
import VirtualScroll from '@/Core/mixins/virtual-scroll';
import TagsPicker from '@/Core/components/TagsPicker';
import FieldSelector from './FieldSelector';
import FieldsMaps from './FieldsMaps';

import { DEFAULT_SAMPLE_DATA_SIZE, SAMPLE_DATA_SIZES } from '../../../config/import-file';

const { mapState: mapContactState, mapActions: mapContactActions } =
  createNamespacedHelpers('contact');
const { mapState: mapImportFileState, mapMutations: mapImportFileMutations } = createNamespacedHelpers('importFile');

const {
  mapState: mapContactsImportState,
  mapActions: mapContactsImportActions,
  mapMutations: mapContactsImportMutations,
} = createNamespacedHelpers('contactsImport');

export default {
  components: {
    TagsPicker,
    FieldSelector,
    PaginationLimit,
    FieldsMaps,
    LiondeskDialog,
  },
  mixins: [VirtualScroll],
  async created() {
    this.currentDate = Date.now();
    if (!this.tags) {
      this.findTags();
    }
  },
  async mounted() {
    this.currentDate = Date.now();
    this.container = document.querySelector('.sample-table').querySelector('.v-table__overflow');
    const virtualScrollOptions = {
      items: this.sampleItems,
      container: this.container,
      rowHeight: 48,
      buffer: 10,
      minPageSize: 100,
    };
    await this.initializeVirtualScroller(virtualScrollOptions);
    this.container.addEventListener('scroll', this.onVirtualScroll);
    window.addEventListener('resize', this.resizeContainer);
    this.resizeContainer();
  },
  beforeDestroy() {
    if (this.container) {
      this.container.removeEventListener('scroll', this.onVirtualScroll);
    }
    window.removeEventListener('resize', this.resizeContainer);
  },
  data() {
    return {
      globalTags: [],
      fieldsMap: {},
      excludedFields: {},
      hideFields: [],
      currentDate: null,
      showNoFullMappedFieldsWarning: false,
      container: null,
      sampleSize: DEFAULT_SAMPLE_DATA_SIZE,
      sampleSizes: SAMPLE_DATA_SIZES,
    };
  },
  computed: {
    ...mapContactsImportState({ importLoading: 'loading', importError: 'error' }),
    ...mapImportFileState({ importFileData: 'data' }),
    ...mapContactState(['tags']),
    headers() {
      if (!this.importFileData) {
        return [];
      }

      const { fields } = this.importFileData;
      return fields.map(field => {
        return {
          text: field,
          value: field,
          align: 'left',
          sortable: false,
        };
      });
    },
    sampleItems() {
      if (!this.importFileData) {
        return [];
      }

      const { sample_data: sampleData } = this.importFileData;
      return sampleData.slice(0, this.sampleSize);
    },
    disabledImport() {
      return !this.hideFields.length;
    },
    systemTag() {
      const date = new Date(this.currentDate).toISOString().split('T')[0];
      return `Contact Import ${date}`;
    },
    tagInfo() {
      return `A tag named "${this.systemTag}" will be automatically added to all contact you import today. You can also select or add your own tags.`;
    },
    showStartImportError: {
      get() {
        return !!this.importError;
      },
      set(value) {
        this.clearMessages();
      },
    },
  },
  methods: {
    ...mapContactsImportMutations(['clearMessages']),
    ...mapContactsImportActions(['startImportProcess']),
    ...mapContactActions(['createTag', 'findTags']),
    ...mapImportFileMutations({ clearImportFileData: 'clearMessages' }),
    sampleText(index) {
      return `Sample ${index + 1}`;
    },
    hideSelectedFields() {
      this.hideFields = Object.keys(this.fieldsMap).map(field => this.fieldsMap[field]);
    },
    matchField(systemField, userField) {
      if (systemField) {
        this.fieldsMap = {
          ...this.fieldsMap,
          [userField]: systemField,
        };
        delete this.excludedFields[userField];
      } else {
        const { [userField]: removedField, ...restFields } = this.fieldsMap;
        this.excludedFields[userField] = true;
        this.fieldsMap = { ...restFields };
      }

      this.hideSelectedFields();
    },
    handleSelectFieldsMap(selectedFieldsMap) {
      if (!selectedFieldsMap) {
        this.fieldsMap = {};
        this.hideFields = [];
        return;
      }

      const { map_fields: fieldsMap } = selectedFieldsMap;
      this.fieldsMap = { ...fieldsMap };
      Object.keys(fieldsMap).forEach(userField => {
        if (this.headers.some(header => header.value === userField)) return;

        delete this.fieldsMap[userField];
      });

      this.hideSelectedFields();
    },
    allFieldsMapped() {
      const totalFields = this.headers.length;
      const totalMapped = Object.keys(this.fieldsMap).length;
      const totalExcluded = Object.keys(this.excludedFields).length;

      return totalFields === totalMapped + totalExcluded;
    },
    async handleStartContactsImport() {
      const payload = {
        file_id: this.importFileData.file_id,
        file_name: this.importFileData.file_name,
        file_headers: this.importFileData.fields,
        map_fields: this.fieldsMap,
        tags: [this.systemTag, ...this.globalTags],
      };
      await this.startImportProcess(payload);
      if (this.importError) {
        return
      }
      this.clearMessages();
      this.clearImportFileData();
      this.$emit('startImport');
    },
    async onClickStartImport() {
      if (!this.allFieldsMapped()) {
        this.showNoFullMappedFieldsWarning = true;
        return
      }

      await this.handleStartContactsImport();
    },
    async onClickStartImportIgnoreUnmappedFields() {
      this.showNoFullMappedFieldsWarning = false;
      await this.handleStartContactsImport();
    },
    handleGoBack() {
      this.showNoFullMappedFieldsWarning = false;
    },
    resizeContainer(e) {
      if (this.container) {
        this.updateVirtualScrollerContainerHeight(this.container.offsetHeight);
      }
      this.onVirtualScroll();
    },
    setSampleSize(newSize) {
      this.sampleSize = newSize;
    },
    handlePreviousStep() {
      this.$emit('click:prev');
    }
  },
  watch: {
    sampleItems() {
      this.updateVirtualScrollerItems(this.sampleItems);
      if (this.container) {
        this.virtualScrollToTop();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sample_text {
  font-weight: 500;
}
.unmapped-title {
  font-size: 16px;
  font-weight: 700;
}
.section-text {
  font-size: 16px;
}
.header {
  border-bottom: 1px solid black;
}
.sample-table /deep/ .v-table__overflow {
  overflow-y: auto;
  max-height: 350px;
  tbody tr td:first-child:not(:last-child),
  thead tr th:first-child {
    background-color: #f1f5f7;
    position: sticky;
    left: 0;
    z-index: 3;
  }
  thead tr th {
    box-shadow: inset 0px 1px 0px #a6a6a655;
    -webkit-box-shadow: inset 0px 1px 0px #a6a6a655;
  }

  thead {
    background-color: #fff;
    position: sticky;
    top: 0;
    z-index: 4;
    box-shadow: 1px 1px 0px #a6a6a655;
    -webkit-box-shadow: 1px 1px 0px #a6a6a655;
  }
}

.skeleton-loader {
  background: url('/static/img/cell-skeleton-loader.svg'),
    repeating-linear-gradient(#fdfdfd, #fdfdfd 47px, #dddddd 48px);
  background-size: 100% 48px, auto;
}

.sample-size {
  margin-bottom: -15px;
}
</style>